/**
 * Device preference memory: which camera, microphone and speaker the participant used last, the
 * display-name convenience prefill, the remembered join device state (joinMicOn / joinCamOn —
 * the participant decides how they enter every call), the mirror option and the background
 * mode. The core load/save functions are pure and take an injectable
 * key-value storage, so tests never touch the real localStorage; corrupted or unexpected content
 * degrades to empty preferences instead of throwing.
 */

import type {
  BackgroundMode,
  BackgroundQualitySetting,
  ResolvedBackgroundQuality,
} from '../media/backgroundProcessor';
import type { CameraQualitySetting } from './publishQuality';
import type { BlurStrength, VideoFilter } from '../media/videoEffects';
import { BLUR_STRENGTHS, VIDEO_FILTERS } from '../media/videoEffects';
import type { FaceMaskId } from '../media/faceMasks';
import { FACE_MASKS } from '../media/faceMasks';

export const DEVICE_PREFS_STORAGE_KEY = 'vc.devicePrefs';

/**
 * Version of the persisted preference document. Version 1 carried devices, the prejoin
 * options and the background settings; version 2 adds the three microphone processing
 * switches (noiseSuppression / echoCancellation / autoGainControl) and, still under v2,
 * the video-effect choices (blurStrength / videoFilter / mask) and the remembered join
 * device state (joinMicOn / joinCamOn), which supersedes the v1 "audioJoinEnabled" option:
 * a stored audioJoinEnabled seeds the first joinMicOn (the option carried the same entry
 * audio decision), then it is dropped. Version 3 adds the camera publish-quality override
 * (camQuality). A document without a version field reads as v1; migrating to a newer
 * version fills the new keys with their defaults (microphone switches on, medium blur,
 * no color filter, no mask, entry mic and camera on, camera quality auto) and keeps
 * every carried field as-is.
 */
export const DEVICE_PREFS_SCHEMA_VERSION = 3;

/** Last used devices, display name, prejoin option toggles and the background mode; null means "not remembered". */
export interface DevicePrefs {
  readonly cameraDeviceId: string | null;
  readonly micDeviceId: string | null;
  readonly displayName: string | null;
  /** Last used audio-output (speaker) device id; null means the system default. */
  readonly speakerDeviceId: string | null;
  /**
   * Remembered entry microphone state: true = the participant last left the prejoin with the
   * microphone on, so the next join publishes it (unmuted); false = the mic stays off (no
   * track) until they unmute from the control bar. Supersedes the removed audioJoinEnabled
   * option, whose stored value seeds this field in the migration.
   */
  readonly joinMicOn: boolean;
  /** Remembered entry camera state: true = the next join publishes the camera, false = avatar. */
  readonly joinCamOn: boolean;
  /** "Mirror video" prejoin option; true by default (self-view mirrored). */
  readonly mirrorVideo: boolean;
  /** Background treatment chosen on the prejoin screen; "blur" by default. */
  readonly bgMode: BackgroundMode;
  /** Background quality setting; "auto" by default (the processor benchmarks once per start). */
  readonly bgQuality: BackgroundQualitySetting;
  /**
   * Quality level the last "auto" benchmark resolved to (shown as "Auto · Eco/Max" in the UI);
   * null when the setting is not auto or no benchmark has run yet.
   */
  readonly resolvedQuality: ResolvedBackgroundQuality | null;
  /** Background blur strength preset; "medium" by default (the pre-effects look). */
  readonly blurStrength: BlurStrength;
  /** Whole-frame color filter preset; "none" by default. */
  readonly videoFilter: VideoFilter;
  /** Face-mask overlay preset (computed locally on the device); "none" by default. */
  readonly mask: FaceMaskId;
  /**
   * Camera publish-quality override for the session tier: "auto" resolves the tier
   * from the machine's hardware signals (macOS -> high; very weak machines -> low),
   * a pinned value forces that tier for capture AND publish. "auto" by default.
   */
  readonly camQuality: CameraQualitySetting;
  /** Browser noiseSuppression processing on the microphone; true (the browser default). */
  readonly micNoiseSuppression: boolean;
  /** Browser echoCancellation processing on the microphone; true (the browser default). */
  readonly micEchoCancellation: boolean;
  /** Browser autoGainControl processing on the microphone; true (the browser default). */
  readonly micAutoGainControl: boolean;
}

export const EMPTY_DEVICE_PREFS: DevicePrefs = {
  cameraDeviceId: null,
  micDeviceId: null,
  displayName: null,
  speakerDeviceId: null,
  joinMicOn: true,
  joinCamOn: true,
  mirrorVideo: true,
  bgMode: 'blur',
  bgQuality: 'auto',
  resolvedQuality: null,
  blurStrength: 'medium',
  videoFilter: 'none',
  mask: 'none',
  camQuality: 'auto',
  micNoiseSuppression: true,
  micEchoCancellation: true,
  micAutoGainControl: true,
};

/** Structural slice of Storage (localStorage) the preference store needs. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readDeviceId(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  return value.trim().length > 0 ? value : null;
}

function readDisplayName(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 && trimmed.length <= 64 ? trimmed : null;
}

/** Reads a boolean option; anything but a real boolean falls back to the default. */
function readFlag(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

const BACKGROUND_MODES: readonly BackgroundMode[] = ['none', 'blur', 'office'];

/** Reads the background mode; anything but a known mode falls back to the default. */
function readBackgroundMode(value: unknown, fallback: BackgroundMode): BackgroundMode {
  return typeof value === 'string' && (BACKGROUND_MODES as readonly string[]).includes(value)
    ? (value as BackgroundMode)
    : fallback;
}

const BACKGROUND_QUALITIES: readonly BackgroundQualitySetting[] = ['auto', 'low', 'high'];

/** Reads the background quality setting; anything but a known value falls back to the default. */
function readBackgroundQuality(
  value: unknown,
  fallback: BackgroundQualitySetting,
): BackgroundQualitySetting {
  return typeof value === 'string' && (BACKGROUND_QUALITIES as readonly string[]).includes(value)
    ? (value as BackgroundQualitySetting)
    : fallback;
}

const RESOLVED_QUALITIES: readonly ResolvedBackgroundQuality[] = ['low', 'high'];

/** Reads the resolved quality level; anything but a known level falls back to null. */
function readResolvedQuality(value: unknown): ResolvedBackgroundQuality | null {
  return typeof value === 'string' && (RESOLVED_QUALITIES as readonly string[]).includes(value)
    ? (value as ResolvedBackgroundQuality)
    : null;
}

const CAMERA_QUALITIES: readonly CameraQualitySetting[] = ['auto', 'low', 'medium', 'high'];

/** Reads the camera publish-quality override; anything else falls back to "auto". */
function readCameraQuality(value: unknown, fallback: CameraQualitySetting): CameraQualitySetting {
  return typeof value === 'string' && (CAMERA_QUALITIES as readonly string[]).includes(value)
    ? (value as CameraQualitySetting)
    : fallback;
}

/** Reads the blur strength preset; anything but a known preset falls back to the default. */
function readBlurStrength(value: unknown, fallback: BlurStrength): BlurStrength {
  return typeof value === 'string' && (BLUR_STRENGTHS as readonly string[]).includes(value)
    ? (value as BlurStrength)
    : fallback;
}

/** Reads the video filter preset; anything but a known preset falls back to the default. */
function readVideoFilter(value: unknown, fallback: VideoFilter): VideoFilter {
  return typeof value === 'string' && (VIDEO_FILTERS as readonly string[]).includes(value)
    ? (value as VideoFilter)
    : fallback;
}

/** Reads the face-mask preset; anything but a known preset falls back to the default. */
function readFaceMask(value: unknown, fallback: FaceMaskId): FaceMaskId {
  return typeof value === 'string' && (FACE_MASKS as readonly string[]).includes(value)
    ? (value as FaceMaskId)
    : fallback;
}

/**
 * Migrates a parsed preference document to the current schema. v1 documents (no version
 * field, no audio-effect keys) keep every carried field and gain the defaults for the
 * newer keys (microphone switches on, medium blur, no color filter); v2 documents
 * round-trip. Unknown fields are dropped. Pure: never throws, anything unexpected
 * degrades to the default.
 */
export function migrateDevicePrefsDocument(document: Record<string, unknown>): DevicePrefs {
  // The version currently only marks the presence of the newer keys; both shapes are
  // read the same way because every reader falls back to the current defaults. The
  // explicit variable documents the contract and keeps future v1->vN migrations in one place.
  const version = typeof document.schemaVersion === 'number' ? document.schemaVersion : 1;
  return version >= 1
    ? {
        cameraDeviceId: readDeviceId(document.cameraDeviceId),
        micDeviceId: readDeviceId(document.micDeviceId),
        displayName: readDisplayName(document.displayName),
        speakerDeviceId: readDeviceId(document.speakerDeviceId),
        joinMicOn: readFlag(
          document.joinMicOn,
          // Legacy seed: the removed "join with audio enabled" option carried the same entry
          // audio decision, so its stored value becomes the first remembered mic state.
          readFlag(document.audioJoinEnabled, EMPTY_DEVICE_PREFS.joinMicOn),
        ),
        joinCamOn: readFlag(document.joinCamOn, EMPTY_DEVICE_PREFS.joinCamOn),
        mirrorVideo: readFlag(document.mirrorVideo, EMPTY_DEVICE_PREFS.mirrorVideo),
        bgMode: readBackgroundMode(document.bgMode, EMPTY_DEVICE_PREFS.bgMode),
        bgQuality: readBackgroundQuality(document.bgQuality, EMPTY_DEVICE_PREFS.bgQuality),
        resolvedQuality: readResolvedQuality(document.resolvedQuality),
        blurStrength: readBlurStrength(document.blurStrength, EMPTY_DEVICE_PREFS.blurStrength),
        videoFilter: readVideoFilter(document.videoFilter, EMPTY_DEVICE_PREFS.videoFilter),
        mask: readFaceMask(document.mask, EMPTY_DEVICE_PREFS.mask),
        camQuality: readCameraQuality(document.camQuality, EMPTY_DEVICE_PREFS.camQuality),
        micNoiseSuppression: readFlag(
          document.micNoiseSuppression,
          EMPTY_DEVICE_PREFS.micNoiseSuppression,
        ),
        micEchoCancellation: readFlag(
          document.micEchoCancellation,
          EMPTY_DEVICE_PREFS.micEchoCancellation,
        ),
        micAutoGainControl: readFlag(
          document.micAutoGainControl,
          EMPTY_DEVICE_PREFS.micAutoGainControl,
        ),
      }
    : EMPTY_DEVICE_PREFS;
}

/**
 * Parses the persisted preference document. Anything unexpected — missing entry, invalid JSON,
 * non-object JSON, fields of the wrong type — yields {@link EMPTY_DEVICE_PREFS} or drops just the
 * broken field; the function never throws.
 */
export function loadDevicePrefs(
  storage: KeyValueStorage,
  key: string = DEVICE_PREFS_STORAGE_KEY,
): DevicePrefs {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return EMPTY_DEVICE_PREFS;
  }
  if (raw === null || raw.length === 0) {
    return EMPTY_DEVICE_PREFS;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return EMPTY_DEVICE_PREFS;
  }
  if (!isRecord(parsed)) {
    return EMPTY_DEVICE_PREFS;
  }

  return migrateDevicePrefsDocument(parsed);
}

/** Persists preferences as JSON, stamped with the current schema version. Storage failures (quota, blocked access) are swallowed: preference memory is best-effort. */
export function saveDevicePrefs(
  storage: KeyValueStorage,
  prefs: DevicePrefs,
  key: string = DEVICE_PREFS_STORAGE_KEY,
): void {
  try {
    storage.setItem(key, JSON.stringify({ schemaVersion: DEVICE_PREFS_SCHEMA_VERSION, ...prefs }));
  } catch {
    // Best-effort: losing the remembered devices is not worth failing a join over.
  }
}

/** Returns a localStorage-backed storage when available, null in environments where access throws. */
export function browserStorage(): KeyValueStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const probeKey = 'vc.storage.probe';
    window.localStorage.setItem(probeKey, probeKey);
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Loads preferences from the browser storage; empty when storage is unavailable. */
export function loadStoredDevicePrefs(): DevicePrefs {
  const storage = browserStorage();
  return storage === null ? EMPTY_DEVICE_PREFS : loadDevicePrefs(storage);
}

/** Persists preferences to the browser storage; a no-op when storage is unavailable. */
export function storeDevicePrefs(prefs: DevicePrefs): void {
  const storage = browserStorage();
  if (storage !== null) {
    saveDevicePrefs(storage, prefs);
  }
}

/**
 * A small observable preference store over one key-value storage: the in-memory current value
 * is the source for readers, updates persist through the storage and notify subscribers, so a
 * dialog toggle reaches an active room page without a rejoin (no external state library).
 */
export interface DevicePrefsStore {
  /** The current preferences (never null; empty defaults when storage is unavailable). */
  get(): DevicePrefs;
  /** Merges a patch into the current preferences, persists and notifies; returns the result. */
  update(patch: Partial<DevicePrefs>): DevicePrefs;
  /** Subscribes to every update; returns the unsubscribe function. */
  subscribe(listener: (prefs: DevicePrefs) => void): () => void;
}

/** Builds an observable store over the given storage (null degrades to an in-memory store). */
export function createDevicePrefsStore(storage: KeyValueStorage | null): DevicePrefsStore {
  let current = storage === null ? EMPTY_DEVICE_PREFS : loadDevicePrefs(storage);
  const listeners = new Set<(prefs: DevicePrefs) => void>();
  return {
    get(): DevicePrefs {
      return current;
    },
    update(patch: Partial<DevicePrefs>): DevicePrefs {
      current = { ...current, ...patch };
      if (storage !== null) {
        saveDevicePrefs(storage, current);
      }
      for (const listener of listeners) {
        listener(current);
      }
      return current;
    },
    subscribe(listener: (prefs: DevicePrefs) => void): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

let browserStore: DevicePrefsStore | null = null;

/** The process-wide store over localStorage; created lazily so tests can inject their own. */
export function devicePrefsStore(): DevicePrefsStore {
  browserStore ??= createDevicePrefsStore(browserStorage());
  return browserStore;
}
